/**
 * usePublicFlowCompletion — the public flow's source of truth for "what's
 * missing" and progress. Backed by GET /public/flow/instance/completion (engine
 * in apps/api, walked with workflow "public"). Session-scoped, so the query key
 * carries no instanceId.
 *
 * Mirrors the advisor `useFlowCompletion` and reuses `deriveMarkers`, so the
 * marker rules (D27/D28/D35/D36/D38) stay identical across both surfaces. Every
 * public mutation that affects an engine input should invalidate this key (P5).
 */

import { toFlowCompletionSummary } from "@repo/shared";
import type { FlowRequirements } from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { getPublicFlowCompletion } from "@/lib/api/public-flow/flow-instance";
import { useSessionToken } from "@/lib/context/public-session-context";
import {
  type CompletionMarkers,
  deriveMarkers,
} from "@/lib/hooks/use-flow-instances/use-flow-completion";

export const publicFlowCompletionQueryKey = ["publicFlow", "completion"] as const;

export function usePublicFlowCompletion() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: publicFlowCompletionQueryKey,
    queryFn: () => getPublicFlowCompletion(token),
    enabled: !!token,
    // Always considered stale — every value-write invalidates this key (P5).
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const markers = useMemo<CompletionMarkers>(() => deriveMarkers(data?.missing ?? []), [data]);

  return { completion: data ?? null, markers, error, isLoading };
}

/** Invalidate the public completion query — call from value-write `onSuccess` (P5). */
export function useInvalidatePublicFlowCompletion() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
}

/**
 * Imperatively seed the completion cache — used by the submit handler on a 422
 * with the latest FlowRequirements, so the progress bar reflects server truth
 * without an extra round trip. Stores the lean summary the query itself serves,
 * so the 422 body's per-item `steps` never lands in the cache (QL-676).
 */
export function setPublicFlowCompletionCache(
  qc: ReturnType<typeof useQueryClient>,
  requirements: FlowRequirements,
) {
  qc.setQueryData(publicFlowCompletionQueryKey, toFlowCompletionSummary(requirements));
}
